<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { nextTick } from 'vue'; // Import nextTick for notification
import { API_BASE_URL } from '@/config';

// Reactive state for the list of suppliers/contractors
const suppliers = ref([]);

// Reactive state for the new supplier/contractor form input
const newSupplier = ref({
    SupplierNo: '', // Corrected column name based on schema
    SupplierName: '', // Corrected column name based on schema
    ContactName: '',
    ContactTitle: '',
    Address: '',
    City: '',
    Country: '',
    PhoneNumber: '', // Corrected column name based on schema
    Services: '', // Corrected column name based on schema
    AccountNo: '', // Corrected column name based on schema
    EmailAddress: '', // Corrected column name based on schema
    Category: '', // Keeping Category based on form, but note it's not in the schema image
    Active: true // Default to active
});

// Reactive state to hold the supplier/contractor currently being edited (null if not editing)
const editingSupplier = ref(null);

// Reactive state to track the row clicked in the table for Edit/Delete actions
const selectedSupplier = ref(null);


// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!
const API_SUPP_URL = '${API_BASE_URL}/api/suppliers'; // New API endpoint for suppliers


const loading = ref(false); // Loading state for async operations (e.g., fetching, saving, deleting)
const error = ref(null); // Error state for displaying persistent error messages
// State for managing temporary notification messages (success, error, warning)
const notification = ref({
    show: false,
    message: '',
    type: '' // 'success', 'error', 'warning'
});

// Options for the Category dropdown
const categories = ['Supplier', 'Contractor', 'Canceled'];


// --- Computed Properties for v-model Binding ---
// This computed property handles the binding for the form inputs.
// It switches between the 'editingSupplier' and 'newSupplier' objects based on whether editing is active.
const currentSupplier = computed({
    get() {
        return editingSupplier.value || newSupplier.value;
    },
    set(value) {
        if (editingSupplier.value) {
            Object.assign(editingSupplier.value, value);
        } else {
            Object.assign(newSupplier.value, value);
        }
    }
});


// --- CRUD Operations ---

// Fetches all suppliers/contractors from the backend API
const fetchSuppliers = async () => {
    loading.value = true; // Set loading state to true
    error.value = null; // Clear any previous errors
    selectedSupplier.value = null; // Deselect any selected item when refreshing the list
    try {
        // Send GET request to the API endpoint
        const response = await axios.get(API_SUPP_URL);
        // Update the suppliers ref with the data received from the backend
        suppliers.value = response.data;
    } catch (err) {
        console.error('Error fetching suppliers:', err);
        error.value = 'Failed to fetch suppliers. Please try again later.';
        showNotification(error.value, 'error'); // Show an error notification
    } finally {
        loading.value = false; // Set loading state to false regardless of success or failure
    }
};

// Creates a new supplier/contractor by sending data to the backend API
const createSupplier = async () => {
    console.log('Attempting to create supplier. Current newSupplier state:', newSupplier.value);
    // Basic client-side validation: check if required fields are not empty
    // Required fields: SupplierNo, SupplierName, Category
    if (!newSupplier.value.SupplierNo.trim() || !newSupplier.value.SupplierName.trim() || !newSupplier.value.Category) {
        showNotification('Supplier Number, Name, and Category are required.', 'warning');
        console.log('Validation failed: Required fields are empty.');
        return; // Stop the function if validation fails
    }
    console.log('Validation passed.');

    // Construct the payload with fields to be sent to the backend
    const payload = {
        SupplierNo: newSupplier.value.SupplierNo, // Corrected column name
        SupplierName: newSupplier.value.SupplierName, // Corrected column name
        ContactName: newSupplier.value.ContactName || null,
        ContactTitle: newSupplier.value.ContactTitle || null,
        Address: newSupplier.value.Address || null,
        City: newSupplier.value.City || null,
        Country: newSupplier.value.Country || null,
        PhoneNumber: newSupplier.value.PhoneNumber || null, // Corrected column name
        Services: newSupplier.value.Services || null, // Corrected column name
        AccountNo: newSupplier.value.AccountNo || null, // Corrected column name
        EmailAddress: newSupplier.value.EmailAddress || null, // Corrected column name
        Category: newSupplier.value.Category, // Keeping Category
        Active: newSupplier.value.Active // Boolean value
    };

    console.log('Payload being sent:', payload);

    loading.value = true; // Set loading state
    error.value = null; // Clear previous errors
    try {
        // Send POST request to the API endpoint with the new supplier data
        const response = await axios.post(API_SUPP_URL, payload); // Use the cleaned payload
        console.log('Backend response:', response.data);

        // Add the newly created item (returned from the backend) to the local list
        suppliers.value.push(response.data);
        // Clear the form inputs
        resetNewSupplierForm(); // Use a helper function to reset
        showNotification('Supplier/Contractor created successfully!', 'success'); // Show success notification
    } catch (err) {
        console.error('Error creating supplier:', err);
        // Handle specific error responses from the backend
        if (err.response?.status === 409) {
            error.value = 'Supplier Number already exists.';
        } else {
            // This will catch potential 500 errors from the backend
            error.value = err.response?.data || 'Failed to create supplier/contractor.';
        }
        showNotification(error.value, 'error'); // Show error notification
    } finally {
        loading.value = false; // Set loading state to false
    }
};

// Updates an existing supplier/contractor by sending data to the backend API
const updateSupplier = async () => {
    // Ensure we are in editing mode and a supplier is selected
    if (!editingSupplier.value || !selectedSupplier.value) {
        showNotification('No supplier/contractor selected for editing.', 'warning');
        return;
    }
    // Basic client-side validation for updateable fields
    // Required fields: SupplierName, Category
    if (!editingSupplier.value.SupplierName.trim() || !editingSupplier.value.Category) {
        showNotification('Supplier Name and Category are required.', 'warning');
        return;
    }

    // Construct the payload with fields to be sent to the backend
    const payload = {
        SupplierName: editingSupplier.value.SupplierName, // Corrected column name
        ContactName: editingSupplier.value.ContactName || null,
        ContactTitle: editingSupplier.value.ContactTitle || null,
        Address: editingSupplier.value.Address || null,
        City: editingSupplier.value.City || null,
        Country: editingSupplier.value.Country || null,
        PhoneNumber: editingSupplier.value.PhoneNumber || null, // Corrected column name
        Services: editingSupplier.value.Services || null, // Corrected column name
        AccountNo: editingSupplier.value.AccountNo || null, // Corrected column name
        EmailAddress: editingSupplier.value.EmailAddress || null, // Corrected column name
        Category: editingSupplier.value.Category, // Keeping Category
        Active: editingSupplier.value.Active // Boolean value
    };


    loading.value = true; // Set loading state
    error.value = null; // Clear previous errors
    try {
        // Send PUT request to the specific item endpoint using the SupplierNo from the selected item
        const response = await axios.put(`${API_SUPP_URL}/${selectedSupplier.value.SupplierNo}`, payload); // Corrected parameter name

        // Find the index of the updated item in the local list using its SupplierNo
        const index = suppliers.value.findIndex(item => item.SupplierNo === selectedSupplier.value.SupplierNo); // Corrected column name
        if (index !== -1) {
            // Update the item in the local list with the data returned from the backend
            suppliers.value[index] = { ...suppliers.value[index], ...response.data };
        }
         // If the updated item was the one currently selected, update the selected item ref as well
         if (selectedSupplier.value && selectedSupplier.value.SupplierNo === editingSupplier.value.SupplierNo) { // Corrected column name
             selectedSupplier.value = { ...selectedSupplier.value, ...response.data };
         }

        editingSupplier.value = null; // Exit editing mode
        showNotification('Supplier/Contractor updated successfully!', 'success'); // Show success notification
    } catch (err) {
        console.error('Error updating supplier:', err);
        // Handle specific error responses from the backend
        if (err.response?.status === 404) {
            error.value = 'Supplier/Contractor not found.';
        } else if (err.response?.status === 400) {
                 error.value = err.response.data || 'Bad Request: Could not update supplier/contractor.';
             } else if (err.response?.status === 500) {
                 error.value = 'Server error during update.';
             }
        else {
            error.value = 'Failed to update supplier/contractor.';
        }
        showNotification(error.value, 'error'); // Show error notification
    } finally {
        loading.value = false; // Set loading state to false
    }
};

// Deletes a supplier/contractor by their unique SupplierNo
const deleteSupplier = async () => {
    // Ensure a supplier is selected for deletion
    if (!selectedSupplier.value) {
        showNotification('Please select a supplier/contractor to delete.', 'warning');
        return;
    }
    // Get the SupplierNo of the item to delete from the selected item
    const supplierNoToDelete = selectedSupplier.value.SupplierNo; // Corrected column name

    if (confirm(`Are you sure you want to delete supplier/contractor ${supplierNoToDelete}? This may also delete associated records.`)) {
        loading.value = true;
        error.value = null;
        try {
            // Send DELETE request to the specific item endpoint using the SupplierNo
            await axios.delete(`${API_SUPP_URL}/${supplierNoToDelete}`); // Corrected parameter name
            // Remove the deleted item from the local list by filtering
            suppliers.value = suppliers.value.filter(item => item.SupplierNo !== supplierNoToDelete); // Corrected column name
            // Clear selected and editing states if they match the deleted item
            selectedSupplier.value = null;
            if(editingSupplier.value && editingSupplier.value.SupplierNo === supplierNoToDelete) { // Corrected column name
                 editingSupplier.value = null;
            }
            showNotification('Supplier/Contractor deleted successfully!', 'success');
        } catch (err) {
            console.error('Error deleting supplier:', err);
            // Handle specific error responses from the backend
            if (err.response?.status === 404) {
                error.value = 'Supplier/Contractor not found.';
            } else if (err.response?.status === 409) {
                 error.value = err.response.data || 'Conflict: Cannot delete supplier/contractor because it is referenced by other entities.';
             }
            else {
                error.value = 'Failed to delete supplier/contractor.';
            }
            showNotification(error.value, 'error');
        } finally {
            loading.value = false;
        }
    }
};

// --- UI Actions ---

// Selects a row in the table when clicked
const selectSupplier = (item) => {
    selectedSupplier.value = item;

    // If you select an item while currently editing another, cancel the editing
    if (editingSupplier.value) {
        cancelEditing();
    }
    error.value = null; // Clear previous errors when selecting a new row
}

// Initiates the editing process for the currently selected supplier/contractor
const startEditing = () => {
    if (selectedSupplier.value) {
         // Copy selected item data to the editing state to populate the form
         editingSupplier.value = { ...selectedSupplier.value };
         resetNewSupplierForm(); // Clear the new supplier form when starting edit mode
         error.value = null; // Clear any previous errors
    } else {
         showNotification('Please select a supplier/contractor to edit.', 'warning'); // Prompt user if no item is selected
    }
};

// Cancels the current editing process and clears the editing state
const cancelEditing = () => {
    editingSupplier.value = null; // Clear the editing state
    resetNewSupplierForm(); // Clear the new supplier form (as it shares inputs via computed properties)
    error.value = null; // Clear any previous errors
};

// Resets the form to the state for adding a new supplier/contractor
 const addNew = () => {
      editingSupplier.value = null; // Ensure not in editing mode
      selectedSupplier.value = null; // Deselect any row in the table
      resetNewSupplierForm(); // Clear form for new entry
      error.value = null; // Clear any previous errors
 };

 // Helper to reset the new supplier/contractor form
 const resetNewSupplierForm = () => {
      newSupplier.value = {
        SupplierNo: '', // Corrected column name
        SupplierName: '', // Corrected column name
        ContactName: '',
        ContactTitle: '',
        Address: '',
        City: '',
        Country: '',
        PhoneNumber: '', // Corrected column name
        Services: '', // Corrected column name
        AccountNo: '', // Corrected column name
        EmailAddress: '', // Corrected column name
        Category: '', // Keeping Category
        Active: true
      };
 };


// Helper function to show a temporary notification message
const showNotification = (message, type) => {
    notification.value.message = message;
    notification.value.type = type;
    notification.value.show = true;

    nextTick(() => {
         const notificationElement = document.querySelector('.notification');
         if (notificationElement) {
              notificationElement.classList.remove('fade-out');
              void notificationElement.offsetWidth; // Trigger reflow to restart animation
             setTimeout(() => {
                 notificationElement.classList.add('fade-out');
                 notificationElement.addEventListener('transitionend', () => {
                      if (!notificationElement.classList.contains('fade-out')) return; // Ensure it's the fade-out transition
                      notification.value.show = false;
                 }, { once: true }); // Remove listener after it runs once
             }, 5000); // Notification visible for 5 seconds
         } else {
              // Fallback if element not found immediately
              setTimeout(() => {
                  notification.value.show = false;
              }, 5500); // Allow a bit more time
         }
    });
};

// --- Lifecycle Hook ---
// Fetch suppliers/contractors when the component is first mounted to the DOM
onMounted(() => {
    fetchSuppliers();
});
</script>

<template>
  <div class="suppliers-page">
    <div class="container">
      <div class="form-panel">
        <h2>{{ editingSupplier ? 'Edit Supplier/Contractor' : 'Add New Supplier/Contractor' }}</h2>
        <form @submit.prevent="editingSupplier ? updateSupplier() : createSupplier()">
          <div class="form-group">
            <label for="SupplierNo">Supplier Number:</label>
              <input type="text" id="SupplierNo" v-model="currentSupplier.SupplierNo" required
                     :disabled="editingSupplier !== null || loading">
           </div>
          <div class="form-group">
            <label for="SupplierName">Supplier Name:</label> <input type="text" id="SupplierName" v-model="currentSupplier.SupplierName" required :disabled="loading">
           </div>

            <div class="form-group">
                <label for="ContactName">Contact Name:</label>
                <input type="text" id="ContactName" v-model="currentSupplier.ContactName" :disabled="loading">
            </div>

            <div class="form-group">
                <label for="ContactTitle">Contact Title:</label>
                <input type="text" id="ContactTitle" v-model="currentSupplier.ContactTitle" :disabled="loading">
            </div>

            <div class="form-group">
                <label for="Address">Address:</label>
                <input type="text" id="Address" v-model="currentSupplier.Address" :disabled="loading">
            </div>

            <div class="form-group">
                <label for="City">City:</label>
                <input type="text" id="City" v-model="currentSupplier.City" :disabled="loading">
            </div>

            <div class="form-group">
                <label for="Country">Country:</label>
                <input type="text" id="Country" v-model="currentSupplier.Country" :disabled="loading">
            </div>

            <div class="form-group">
                <label for="PhoneNumber">Phone Number:</label> <input type="text" id="PhoneNumber" v-model="currentSupplier.PhoneNumber" :disabled="loading"> </div>

             <div class="form-group">
                <label for="Services">Service:</label> <input type="text" id="Services" v-model="currentSupplier.Services" :disabled="loading"> </div>

            <div class="form-group">
                <label for="AccountNo">Account No:</label> <input type="text" id="AccountNo" v-model="currentSupplier.AccountNo" :disabled="loading"> </div>

            <div class="form-group">
                <label for="EmailAddress">E-Mail:</label> <input type="email" id="EmailAddress" v-model="currentSupplier.EmailAddress" :disabled="loading"> </div>

           <div class="form-group">
                 <label for="Category">Category:</label>
                 <select id="Category" v-model="currentSupplier.Category" required :disabled="loading">
                      <option value="">-- Select Category --</option>
                     <option v-for="category in categories" :key="category" :value="category">
                         {{ category }} </option>
                 </select>
            </div>

            <div class="form-group checkbox-group">
                 <input type="checkbox" id="Active" v-model="currentSupplier.Active" :disabled="loading">
                 <label for="Active">Active</label>
            </div>


          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="loading">
              {{ editingSupplier ? 'Update Supplier' : 'Save Supplier' }}
            </button>
            <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingSupplier" :disabled="loading">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div class="table-panel">
        <h2>Supplier/Contractor List</h2>

          <div v-if="loading && suppliers.length === 0">Loading suppliers/contractors...</div>
          <div v-if="error && suppliers.length === 0 && !loading" class="error-message">{{ error }}</div>

        <div class="table-actions">
            <button @click="addNew" :disabled="loading">Add New</button>
            <button @click="startEditing" :disabled="!selectedSupplier || loading">Edit</button>
            <button @click="deleteSupplier" :disabled="!selectedSupplier || loading">Delete</button>
            <button @click="fetchSuppliers" :disabled="loading">Refresh</button>
          </div>

        <table class="site-table">
          <thead>
            <tr>
              <th>Supplier No</th>
              <th>Supplier Name</th>
              <th>Category</th>
              <th>Active</th>
              <th>Contact Name</th>
              <th>Phone Number</th> <th>E-Mail</th> </tr>
          </thead>
          <tbody>
            <tr v-for="item in suppliers" :key="item.SupplierNo"
                :class="{ 'selected': selectedSupplier && selectedSupplier.SupplierNo === item.SupplierNo }"
                @click="selectSupplier(item)">
                 <td>{{ item.SupplierNo }}</td>
                 <td>{{ item.SupplierName }}</td> <td>{{ item.Category }}</td>
                 <td>{{ item.Active ? 'Yes' : 'No' }}</td>
                 <td>{{ item.ContactName }}</td>
                 <td>{{ item.PhoneNumber }}</td> <td>{{ item.EmailAddress }}</td> </tr>
               <tr v-if="suppliers.length === 0 && !loading">
                 <td colspan="7" style="text-align: center;">No suppliers/contractors found.</td> </tr>
          </tbody>
        </table>
      </div>
    </div>

      <div v-if="notification.show" :class="['notification', notification.type, { 'fade-out': !notification.show }]">
      {{ notification.message }}.
      </div>

  </div>
</template>

<style scoped>
/* Reuse and adapt styles from the employee view */
.suppliers-page { /* Unique class name for this page */
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f4f6f8;
    min-height: calc(100vh - 40px); /* Ensure page takes at least full height */
    color: #333; /* Default text color */
}

.container {
    display: flex;
    gap: 20px;
    max-width: 1400px; /* Adjusted max width for more columns */
    margin: 0 auto;
    flex-wrap: wrap;
    align-items: flex-start; /* Align items to the top */
}

.form-panel, .table-panel {
    background-color: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    min-width: 300px;
     box-sizing: border-box; /* Include padding and border in the element's total width and height */
}

.form-panel {
     flex: 1 1 350px; /* Flex-grow, Flex-shrink, Basis */
     max-width: 400px; /* Optional: Limit max width for form */
}

.table-panel {
     flex: 2 1 800px; /* Adjusted flex basis for more columns */
    overflow-x: auto;
}


h2 {
    color: #333;
    margin-top: 0;
    margin-bottom: 25px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
    font-size: 1.5rem;
    font-weight: 600; /* Slightly bolder heading */
}

.form-group {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Style for the checkbox group */
.form-group.checkbox-group {
    flex-direction: row; /* Align checkbox and label horizontally */
    align-items: center; /* Vertically align items */
    gap: 10px; /* Space between checkbox and label */
}

.form-group label {
    display: block;
    color: #555;
    font-weight: bold;
    font-size: 0.95rem;
}

/* Adjust label for checkbox group */
.form-group.checkbox-group label {
    font-weight: normal; /* Normal weight for checkbox label */
    margin-bottom: 0; /* Remove bottom margin */
}


input[type="text"],
input[type="number"],
input[type="email"],
select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 1rem;
    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="text"]:focus,
input[type="number"]:focus,
input[type="email"]:focus,
select:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
input[type="email"]:disabled,
select:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
    opacity: 0.8;
}

input[type="checkbox"] {
    /* Basic styling for checkbox */
    width: auto; /* Allow checkbox to take its natural width */
    margin-right: 5px; /* Space between checkbox and label */
}


.form-actions {
    margin-top: 30px;
    display: flex;
    gap: 10px;
     flex-wrap: wrap; /* Allow buttons to wrap on small screens */
}

.save-button,
.cancel-button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     min-width: 90px; /* Ensure minimum button width */
     text-align: center;
     flex-grow: 1; /* Allow buttons to grow if needed */
}

.save-button {
    background-color: #28a745; /* Green */
    color: white;
}

.save-button:hover:not(:disabled) {
    background-color: #218838;
}

.cancel-button {
    background-color: #6c757d; /* Gray */
    color: white;
}

.cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
}

button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
}


.table-actions {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.table-actions button {
    padding: 8px 12px;
    border: 1px solid #007bff;
    border-radius: 4px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     flex-grow: 1;
     min-width: 60px;
     text-align: center;
}

/* Specific button styles */
.table-actions button:nth-child(2) { /* Edit button */
    background-color: #ffc107;
    border-color: #ffc107;
    color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
    background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
    background-color: #c82333;
}

.table-actions button:hover:not(:disabled) {
    background-color: #0056b3;
    border-color: #0056b3;
}


.site-table { /* Using site-table class for consistency, but it's for suppliers now */
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-size: 0.95rem;
    border: 1px solid #ddd;
}

.site-table th,
.site-table td {
    padding: 12px;
    border: 1px solid #ddd;
    text-align: left;
    word-break: break-word;
}

.site-table th {
    background-color: #e9ecef;
    font-weight: bold;
    color: #495057;
    text-transform: uppercase;
    font-size: 0.85rem;
}

 .site-table tbody tr:nth-child(even) {
      background-color: #f8f9fa;
 }

.site-table tbody tr:hover {
    background-color: #e9ecef;
    cursor: pointer;
}

.site-table tbody tr.selected {
    background-color: #cfe2ff;
     outline: 2px solid #007bff;
     outline-offset: -2px;
}

 /* Center text in empty cell */
 .site-table td[colspan="7"] { /* Adjust colspan based on number of displayed columns */
      text-align: center;
      font-style: italic;
      color: #666;
 }


/* Responsive adjustments */
@media (max-width: 768px) {
    .container {
        flex-direction: column;
        gap: 15px;
    }

    .form-panel,
    .table-panel {
        margin-bottom: 15px;
        min-width: unset;
        flex: unset;
        width: 100%;
        padding: 15px;
    }

    h2 {
        margin-bottom: 15px;
        padding-bottom: 10px;
        font-size: 1.3rem;
    }

    .form-group {
        margin-bottom: 15px;
    }

    .form-actions {
        margin-top: 20px;
        flex-direction: column;
        gap: 8px;
    }

    .save-button, .cancel-button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }

    .table-actions {
        flex-direction: column;
        gap: 8px;
    }

    .table-actions button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }
}

/* Notification Styling */
.notification {
    position: fixed;
    bottom: 20px;
    left: 20px;
    padding: 15px 20px;
    border-radius: 4px;
    color: white;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
     opacity: 1;
     transition: opacity 0.5s ease-in-out;
}

 .notification.fade-out {
     opacity: 0;
 }


.notification.success {
    background-color: #28a745;
}

.notification.error {
    background-color: #dc3545;
}

.notification.warning {
    background-color: #ffc107;
    color: #212529;
}

.error-message {
    color: #dc3545;
    margin-bottom: 15px;
    padding: 10px;
    border: 1px solid #dc3545;
    background-color: #fff3f3;
    border-radius: 4px;
     font-size: 0.9rem;
}

</style>
